import {
  AlertTriangle,
  Check,
  ChevronRight,
  ExternalLink,
  LogOut,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { ApiError, apiGet, apiPost, apiPut } from '../api/client.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { ErrorBanner } from '../components/ErrorBanner.js';
import { Field, Input, Select } from '../components/Input.js';
import { PageHeader } from '../components/PageHeader.js';
import { useStores } from '../state/store-context.js';

interface Platform {
  id: 'ebay' | 'aliexpress' | 'amazon' | 'temu';
  name: string;
  description: string;
  status: 'live' | 'coming-soon';
  color: string;
  letter: string;
}

const PLATFORMS: Platform[] = [
  {
    id: 'ebay',
    name: 'eBay',
    description: 'Sync listings, orders, and run automated underperformer alerts.',
    status: 'live',
    color: '#0064d2',
    letter: 'e',
  },
  {
    id: 'aliexpress',
    name: 'AliExpress',
    description:
      'Pull hot products and supplier pricing — powers the Trends tab. Affiliate AppKey + AppSecret required.',
    status: 'live',
    color: '#e62e04',
    letter: 'A',
  },
  {
    id: 'amazon',
    name: 'Amazon',
    description: 'Read best-sellers and category trends via the Product Advertising API.',
    status: 'coming-soon',
    color: '#ff9900',
    letter: 'a',
  },
  {
    id: 'temu',
    name: 'Temu',
    description: 'Public listings monitoring once Temu releases a partner API.',
    status: 'coming-soon',
    color: '#f97316',
    letter: 'T',
  },
];

interface EbayKeysForm {
  env: 'production' | 'sandbox';
  appId: string;
  certId: string;
  devId: string;
  ruName: string;
}

interface AliKeysForm {
  appKey: string;
  appSecret: string;
  trackingId: string;
}

function PlatformLogo({ p }: { p: Platform }): JSX.Element {
  return (
    <div
      style={{
        width: 40,
        height: 40,
        borderRadius: 'var(--radius-md)',
        background: p.color,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: 18,
        fontStyle: p.id === 'ebay' ? 'italic' : 'normal',
        flexShrink: 0,
      }}
    >
      {p.letter}
    </div>
  );
}

export function SettingsPage(): JSX.Element {
  const { stores, refresh } = useStores();
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState<Platform['id'] | null>(null);
  const [ebayForm, setEbayForm] = useState<EbayKeysForm | null>(null);
  const [aliForm, setAliForm] = useState<AliKeysForm | null>(null);
  const [aliSaved, setAliSaved] = useState(false);
  const [savingKeys, setSavingKeys] = useState(false);
  const [keysError, setKeysError] = useState<string | null>(null);

  // Owner-only: only the owner can save eBay app keys. Non-owners that hit the
  // unconfigured path will see "ask your admin" copy instead of the form.
  const [isOwner, setIsOwner] = useState<boolean | null>(null);
  useEffect(() => {
    apiGet('/api/admin/settings')
      .then(() => setIsOwner(true))
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 403) setIsOwner(false);
        else setIsOwner(false);
      });
  }, []);

  const startEbayConnect = async (): Promise<void> => {
    setError(null);
    setKeysError(null);
    setConnecting('ebay');
    try {
      const { consentUrl } = await apiPost<{ consentUrl: string }>(
        '/api/stores/connect/start',
        {},
      );
      window.location.href = consentUrl;
    } catch (e) {
      const apiErr = e instanceof ApiError ? e : null;
      // 400 ebay_not_configured or ebay_keys_invalid → open the inline form
      // so the user can correct the values directly without leaving the page.
      if (apiErr?.status === 400 && /ebay/i.test(apiErr.message)) {
        setEbayForm({ env: 'production', appId: '', certId: '', devId: '', ruName: '' });
        // ebay_keys_invalid carries a specific message — surface it so the
        // user knows whether the issue is wrong env, wrong App ID, etc.
        if (apiErr.message && !/not configured/i.test(apiErr.message)) {
          setKeysError(apiErr.message);
        }
      } else {
        setError(apiErr ? apiErr.message : 'Could not start eBay connect.');
      }
    } finally {
      setConnecting(null);
    }
  };

  const saveEbayKeysAndConnect = async (): Promise<void> => {
    if (!ebayForm) return;
    setKeysError(null);
    if (!ebayForm.appId || !ebayForm.certId || !ebayForm.devId || !ebayForm.ruName) {
      setKeysError('All four eBay keys are required to connect.');
      return;
    }
    setSavingKeys(true);
    try {
      const upserts: Array<[string, unknown]> = [
        ['ebay.env', ebayForm.env],
        ['ebay.appId', ebayForm.appId],
        ['ebay.certId', ebayForm.certId],
        ['ebay.devId', ebayForm.devId],
        ['ebay.ruName', ebayForm.ruName],
      ];
      for (const [key, value] of upserts) {
        await apiPut('/api/admin/settings', { key, value });
      }
      setEbayForm(null);
      await startEbayConnect();
    } catch (e) {
      setKeysError(e instanceof Error ? e.message : 'Saving keys failed.');
    } finally {
      setSavingKeys(false);
    }
  };

  const saveAliKeys = async (): Promise<void> => {
    if (!aliForm) return;
    setKeysError(null);
    setAliSaved(false);
    if (!aliForm.appKey || !aliForm.appSecret) {
      setKeysError('AppKey and AppSecret are both required.');
      return;
    }
    setSavingKeys(true);
    try {
      const upserts: Array<[string, unknown]> = [
        ['aliexpress.appKey', aliForm.appKey],
        ['aliexpress.appSecret', aliForm.appSecret],
        ['aliexpress.trackingId', aliForm.trackingId || ''],
      ];
      for (const [key, value] of upserts) {
        await apiPut('/api/admin/settings', { key, value });
      }
      setAliForm(null);
      setAliSaved(true);
      window.setTimeout(() => setAliSaved(false), 3000);
    } catch (e) {
      setKeysError(e instanceof Error ? e.message : 'Saving AliExpress keys failed.');
    } finally {
      setSavingKeys(false);
    }
  };

  const syncNow = async (id: string): Promise<void> => {
    try {
      await apiPost(`/api/stores/${id}/sync-now`, {});
      refresh();
    } catch {
      setError('Sync failed. Check sync history.');
    }
  };

  const disconnect = async (id: string): Promise<void> => {
    if (!confirm('Disconnect this store?')) return;
    await apiPost(`/api/stores/${id}/disconnect`, {});
    refresh();
  };

  const logout = async (): Promise<void> => {
    await apiPost('/api/auth/logout', {});
    window.location.href = '/login';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        title="Stores"
        accent="connected"
        sub="Connect every platform you sell on — eBay live, more on the way."
      />

      {error && <ErrorBanner message={error} onRetry={() => setError(null)} />}

      <Card
        title="Available platforms"
        description="Pick a platform to connect. Live integrations connect via the platform's official OAuth — your password never touches us."
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {PLATFORMS.map((p) => {
            const isEbay = p.id === 'ebay';
            const isAli = p.id === 'aliexpress';
            const live = p.status === 'live';
            const ebayShowingForm = isEbay && ebayForm !== null;
            const aliShowingForm = isAli && aliForm !== null;
            const showingForm = ebayShowingForm || aliShowingForm;
            const handleConnect = (): void => {
              if (isEbay) {
                if (ebayShowingForm) setEbayForm(null);
                else startEbayConnect();
              } else if (isAli) {
                if (aliShowingForm) setAliForm(null);
                else setAliForm({ appKey: '', appSecret: '', trackingId: '' });
              }
            };
            return (
              <div
                key={p.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid var(--c-border)',
                  borderRadius: 'var(--radius-lg)',
                  padding: 14,
                  background: 'var(--c-card)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    width: '100%',
                  }}
                >
                  <PlatformLogo p={p} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 'var(--text-md)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      {p.name}
                      {!live && (
                        <span
                          style={{
                            fontSize: 'var(--text-xs)',
                            color: 'var(--c-muted)',
                            background: 'var(--c-divider)',
                            padding: '2px 8px',
                            borderRadius: 99,
                            fontWeight: 500,
                          }}
                        >
                          Coming soon
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: 'var(--text-base)',
                        color: 'var(--c-muted)',
                        marginTop: 2,
                      }}
                    >
                      {p.description}
                    </div>
                  </div>
                  {live ? (
                    <Button
                      size="sm"
                      icon={showingForm ? undefined : Plus}
                      iconRight={showingForm ? undefined : ChevronRight}
                      loading={connecting === p.id}
                      onClick={handleConnect}
                      variant={showingForm ? 'secondary' : 'primary'}
                      aria-label={
                        showingForm
                          ? `Cancel ${p.name} connect`
                          : `Connect ${p.name} store`
                      }
                    >
                      {showingForm ? 'Cancel' : 'Connect'}
                    </Button>
                  ) : (
                    <Button size="sm" variant="secondary" disabled>
                      Soon
                    </Button>
                  )}
                </div>

                {aliShowingForm && aliForm && (
                  <div
                    style={{
                      marginTop: 16,
                      paddingTop: 16,
                      borderTop: '1px solid var(--c-divider)',
                    }}
                  >
                    {isOwner === false ? (
                      <div
                        style={{
                          padding: 12,
                          background: 'var(--c-warn-bg)',
                          color: 'var(--c-warn)',
                          borderRadius: 'var(--radius-md)',
                          fontSize: 'var(--text-md)',
                          display: 'flex',
                          gap: 10,
                        }}
                      >
                        <AlertTriangle size={18} style={{ flexShrink: 0 }} />
                        <span>
                          AliExpress keys aren't configured yet. Please ask the workspace
                          owner to set them up.
                        </span>
                      </div>
                    ) : (
                      <>
                        <p
                          style={{
                            fontSize: 'var(--text-base)',
                            color: 'var(--c-muted)',
                            marginBottom: 14,
                            lineHeight: 1.55,
                          }}
                        >
                          From{' '}
                          <a
                            href="https://portals.aliexpress.com"
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              color: 'var(--c-primary)',
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 3,
                            }}
                          >
                            AliExpress Affiliate portal
                            <ExternalLink size={12} />
                          </a>{' '}
                          → App Console. AppKey and AppSecret are required; Tracking ID is
                          optional but recommended so eligible deep links earn commission.
                          Keys are saved encrypted.
                        </p>
                        <Field label="AppKey" htmlFor="ali-appkey">
                          <Input
                            id="ali-appkey"
                            value={aliForm.appKey}
                            onChange={(e) =>
                              setAliForm({ ...aliForm, appKey: e.target.value })
                            }
                          />
                        </Field>
                        <Field label="AppSecret" htmlFor="ali-appsecret">
                          <Input
                            id="ali-appsecret"
                            type="password"
                            value={aliForm.appSecret}
                            onChange={(e) =>
                              setAliForm({ ...aliForm, appSecret: e.target.value })
                            }
                          />
                        </Field>
                        <Field label="Tracking ID" hint="optional" htmlFor="ali-tracking">
                          <Input
                            id="ali-tracking"
                            value={aliForm.trackingId}
                            onChange={(e) =>
                              setAliForm({ ...aliForm, trackingId: e.target.value })
                            }
                            placeholder="default"
                          />
                        </Field>
                        {keysError && (
                          <p
                            style={{
                              color: 'var(--c-danger)',
                              fontSize: 'var(--text-md)',
                              marginBottom: 12,
                            }}
                          >
                            {keysError}
                          </p>
                        )}
                        <Button icon={Check} loading={savingKeys} onClick={saveAliKeys}>
                          Save AliExpress keys
                        </Button>
                      </>
                    )}
                  </div>
                )}

                {isAli && aliSaved && !aliShowingForm && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: 10,
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--c-success-bg)',
                      color: 'var(--c-success)',
                      fontSize: 'var(--text-base)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <Check size={14} /> AliExpress keys saved. Visit Trends to pull data.
                  </div>
                )}

                {ebayShowingForm && (
                  <div
                    style={{
                      marginTop: 16,
                      paddingTop: 16,
                      borderTop: '1px solid var(--c-divider)',
                    }}
                  >
                    {isOwner === false ? (
                      <div
                        style={{
                          padding: 12,
                          background: 'var(--c-warn-bg)',
                          color: 'var(--c-warn)',
                          borderRadius: 'var(--radius-md)',
                          fontSize: 'var(--text-md)',
                          display: 'flex',
                          gap: 10,
                        }}
                      >
                        <AlertTriangle size={18} style={{ flexShrink: 0 }} />
                        <span>
                          eBay app keys aren't configured yet. Please ask the workspace
                          owner to set them up before connecting a store.
                        </span>
                      </div>
                    ) : (
                      <>
                        <p
                          style={{
                            fontSize: 'var(--text-base)',
                            color: 'var(--c-muted)',
                            marginBottom: 14,
                            lineHeight: 1.55,
                          }}
                        >
                          Paste your eBay keyset values. Find them at{' '}
                          <a
                            href="https://developer.ebay.com/my/keys"
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              color: 'var(--c-primary)',
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 3,
                            }}
                          >
                            developer.ebay.com → Application Keysets
                            <ExternalLink size={12} />
                          </a>
                          . All four must come from the same keyset matching the environment
                          below. Keys are saved encrypted, not stored anywhere else.
                        </p>
                        <Field label="Environment" htmlFor="ebay-env-inline">
                          <Select
                            id="ebay-env-inline"
                            value={ebayForm.env}
                            onChange={(e) =>
                              setEbayForm({
                                ...ebayForm,
                                env: e.target.value as 'production' | 'sandbox',
                              })
                            }
                          >
                            <option value="production">Production</option>
                            <option value="sandbox">Sandbox</option>
                          </Select>
                        </Field>
                        <Field label="App ID (Client ID)" htmlFor="ebay-appid-inline">
                          <Input
                            id="ebay-appid-inline"
                            value={ebayForm.appId}
                            onChange={(e) =>
                              setEbayForm({ ...ebayForm, appId: e.target.value })
                            }
                            placeholder="username-AppName-PRD-abc123-def4567"
                          />
                        </Field>
                        <Field label="Cert ID (Client Secret)" htmlFor="ebay-cert-inline">
                          <Input
                            id="ebay-cert-inline"
                            type="password"
                            value={ebayForm.certId}
                            onChange={(e) =>
                              setEbayForm({ ...ebayForm, certId: e.target.value })
                            }
                          />
                        </Field>
                        <Field label="Dev ID" htmlFor="ebay-dev-inline">
                          <Input
                            id="ebay-dev-inline"
                            value={ebayForm.devId}
                            onChange={(e) =>
                              setEbayForm({ ...ebayForm, devId: e.target.value })
                            }
                          />
                        </Field>
                        <Field
                          label="RuName"
                          hint="not a URL — the alias eBay shows in User Tokens"
                          htmlFor="ebay-ru-inline"
                        >
                          <Input
                            id="ebay-ru-inline"
                            value={ebayForm.ruName}
                            onChange={(e) =>
                              setEbayForm({ ...ebayForm, ruName: e.target.value })
                            }
                            placeholder="YourName-AppName-XYZ-abc123"
                          />
                        </Field>
                        {keysError && (
                          <p
                            style={{
                              color: 'var(--c-danger)',
                              fontSize: 'var(--text-md)',
                              marginBottom: 12,
                            }}
                          >
                            {keysError}
                          </p>
                        )}
                        <Button
                          icon={Check}
                          loading={savingKeys}
                          onClick={saveEbayKeysAndConnect}
                        >
                          Save & connect to eBay
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <Card
        title="Connected stores"
        description="Stores you've already linked. Sync pulls fresh listings and orders."
      >
        {stores.length === 0 ? (
          <p style={{ fontSize: 'var(--text-md)', color: 'var(--c-muted)' }}>
            No stores connected yet.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {stores.map((s) => (
              <div
                key={s.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: 14,
                  border: '1px solid var(--c-border)',
                  borderRadius: 'var(--radius-md)',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: 'var(--text-md)', marginBottom: 4 }}>
                    {s.storeName}
                  </div>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--c-muted)' }}>
                    Connected {new Date(s.connectedAt).toLocaleDateString()} · Last sync:{' '}
                    {s.lastSyncRunAt ? new Date(s.lastSyncRunAt).toLocaleString() : 'never'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button size="sm" icon={RefreshCw} onClick={() => syncNow(s.id)}>
                    Sync now
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    icon={Trash2}
                    onClick={() => disconnect(s.id)}
                  >
                    Disconnect
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div>
        <Button variant="danger" icon={LogOut} onClick={logout}>
          Sign out
        </Button>
      </div>
    </div>
  );
}
